import React, { useCallback, useMemo } from "react";
import { calculateStrainDamage } from "~/rules/strain";
import { Button } from "~/widgets/Button/Button";
import GlossaryInfoIcon from "~/widgets/GlossaryInfoIcon/GlossaryInfoIcon";
import { AppCommand } from "~/types/commands";
import { useFocus } from "~/contexts/FocusContext";
import { STRAIN_ACTION_INCREASE, STRAIN_ACTION_DECREASE } from "~/pages/CharacterSheet/characterActions";
import styles from "~/pages/CharacterSheet/widgets/StrainTracker/StrainTracker.module.scss";

export interface StrainTrackerProps {
  value: number;
  onChange: (value: number) => void;
  onApplyDamage?: (damage: number) => void;
  onCommand?: (command: AppCommand) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /**
   * Size variant for the component.
   * - 'default': Full display with info icon, tooltip, and warning box
   * - 'compact': Minimal display with inline End Combat button, no tooltip
   */
  size?: 'default' | 'compact';
}

/**
 * StrainTracker widget for tracking strain accumulated during combat.
 *
 * From the rules:
 * - Strain is accumulated when taking Breather actions (1 per effect chosen)
 * - After combat ends, for every 2 Strain, take 1 damage directly to HP
 * - Strain does NOT clear automatically - must be manually cleared
 *
 * Displays current strain with increment/decrement buttons and shows
 * the calculated post-combat damage.
 *
 * Keyboard interaction:
 * - When focused, use Arrow Up to increase strain or Arrow Down to decrease strain
 * - Arrow keys emit strain-increase/strain-decrease commands when pressed
 * - Press 'S' to focus the strain tracker from elsewhere
 *
 * @param size - 'default' shows full layout with tooltip, 'compact' shows minimal inline layout
 * @param compact - Alternative to size prop; when true, uses compact layout
 * @param onCommand - Callback to emit strain-increase/strain-decrease commands on arrow key presses
 */
const StrainTracker = React.forwardRef<HTMLDivElement, StrainTrackerProps>(
  ({ value, onChange, onApplyDamage, onCommand: _onCommand, onFocus, onBlur, size = 'default' }, ref) => {
    const handleIncrement = useCallback(() => {
      onChange(value + 1);
    }, [value, onChange]);

    const handleDecrement = useCallback(() => {
      onChange(Math.max(0, value - 1));
    }, [value, onChange]);

    const handleCommandInternal = useCallback((command: AppCommand) => {
      switch (command.type) {
        case "strain-increase":
          handleIncrement();
          break;
        case "strain-decrease":
          handleDecrement();
          break;
      }
    }, [handleIncrement, handleDecrement]);

    const { activate } = useFocus(
      "StrainTracker",
      useMemo(() => ({ value }), [value]),
      handleCommandInternal,
      [
        STRAIN_ACTION_INCREASE,
        ...(value > 0 ? [STRAIN_ACTION_DECREASE] : [])
      ]
    );

    const handleClear = useCallback(() => {
      onChange(0);
    }, [onChange]);

    const postCombatDamage = calculateStrainDamage(value);
    const hasStrain = value > 0;
    const isCompact = size === 'compact';

    const handleEndCombat = useCallback(() => {
      if (postCombatDamage > 0 && onApplyDamage) {
        onApplyDamage(postCombatDamage);
        // Note: Strain is NOT cleared per rules - it persists after combat
      }
    }, [postCombatDamage, onApplyDamage]);

    // Compact variant: vertical stack to minimize width
    if (isCompact) {
      const compactContainerClass = [
        styles.compactItem,
        hasStrain ? styles.compactItemStrained : "",
      ].filter(Boolean).join(" ");

      return (
        <div
          ref={ref}
          className={compactContainerClass}
          tabIndex={0}
          onFocus={() => {
            activate();
            onFocus?.();
          }}
          onBlur={onBlur}
          role="spinbutton"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-label={`Strain: ${value}${postCombatDamage > 0 ? `, ${postCombatDamage} damage after combat` : ""}. Use up and down arrows to adjust.`}
          title={hasStrain && postCombatDamage > 0 ? `${postCombatDamage} HP damage after combat` : undefined}
        >
          <span className={`${styles.compactLabel} ${hasStrain ? styles.compactLabelStrained : ""}`}>
            Strain
          </span>
          <div className={styles.compactControls}>
            <button
              type="button"
              onClick={handleDecrement}
              className={`${styles.compactButton} ${hasStrain ? styles.compactButtonStrained : ""}`}
              disabled={value === 0}
              tabIndex={-1}
              aria-hidden="true"
            >
              -
            </button>
            <span className={`${styles.compactValue} ${hasStrain ? styles.compactValueStrained : ""}`}>
              {value}
            </span>
            <button
              type="button"
              onClick={handleIncrement}
              className={`${styles.compactButton} ${hasStrain ? styles.compactButtonStrained : ""}`}
              tabIndex={-1}
              aria-hidden="true"
            >
              +
            </button>
          </div>
          {onApplyDamage && postCombatDamage > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEndCombat();
              }}
              className={styles.compactEndButton}
              title="Apply strain damage to HP"
              tabIndex={-1}
            >
              {postCombatDamage} Dmg
            </button>
          )}
        </div>
      );
    }

    // Default variant: full display
    return (
      <>
        <div
          ref={ref}
          className={`${styles.container} ${hasStrain ? styles.containerStrained : styles.containerNormal}`}
          tabIndex={0}
          onFocus={() => {
            activate();
            onFocus?.();
          }}
          onBlur={onBlur}
        >
          <div className={styles.header}>
            <div className={styles.titleWrapper}>
              <span className={`${styles.title} ${hasStrain ? styles.titleStrained : styles.titleNormal}`}>
                Strain
              </span>
              <GlossaryInfoIcon
                glossaryId="Strain"
                className={`${styles.infoIcon} ${hasStrain ? styles.infoIconStrained : styles.infoIconNormal}`}
              />
            </div>
            <button
              type="button"
              onClick={handleClear}
              className={`${styles.clearButton} ${hasStrain ? styles.clearButtonStrained : styles.clearButtonNormal}`}
              disabled={value === 0}
              aria-label="Clear strain"
            >
              Clear
            </button>
          </div>

          <div className={styles.controls}>
            <button
              type="button"
              onClick={handleDecrement}
              className={`${styles.button} ${hasStrain ? styles.buttonStrained : styles.buttonNormal}`}
              disabled={value === 0}
              aria-label="Decrease strain"
            >
              -
            </button>

            <div className={styles.valueDisplay}>
              <span className={`${styles.valueNumber} ${hasStrain ? styles.valueNumberStrained : styles.valueNumberNormal}`}>
                {value}
              </span>
              <span className={`${styles.valueLabel} ${hasStrain ? styles.valueLabelStrained : styles.valueLabelNormal}`}>
                Strain
              </span>
            </div>

            <button
              type="button"
              onClick={handleIncrement}
              className={`${styles.button} ${hasStrain ? styles.buttonStrained : styles.buttonNormal}`}
              aria-label="Increase strain"
            >
              +
            </button>
          </div>

          {value > 0 && (
            <div className={styles.warningBox}>
              <div className={styles.warningContent}>
                <span className={styles.warningIcon}>⚠</span>
                <span className={styles.warningText}>
                  After combat: <strong className={styles.warningDamage}>{postCombatDamage} HP damage</strong>
                </span>
              </div>
              {onApplyDamage && postCombatDamage > 0 && (
                <Button
                  type="button"
                  onClick={handleEndCombat}
                  variant="warning"
                  size="sm"
                  title="Apply strain damage to HP (strain persists)"
                >
                  End Combat
                </Button>
              )}
            </div>
          )}
        </div>
      </>
    );
  });

export default StrainTracker;
